import { useEffect, useMemo, useRef } from "react";
import { useSnackbar } from "notistack";
import styled from "styled-components";
import { useDebouncedCallback } from "use-debounce";

import { Button, Table, TableInstance } from "@monolith-forensics/monolith-ui";

import AuditsAPI from "@/api/Audits/Audits";
import { useTableState } from "@/Features/_shared";
import { getDateFormat } from "@/utils/date-format";

import { AuditLogsQuery, GetAuditLogsQuery } from "../audits.query";
import AuditLogColumnDefinitions from "./AuditLogColumnDefinitions";

const DEFAULT_PAGE_SIZE = 100;

const StyledContent = styled.div`
  position: relative;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export type AuditLogsTableProps = {
  // Scope for the log list — an object (storage/evidence item) or an audit.
  query: GetAuditLogsQuery;
  stateStoreKey: string;
};

// Audit Logs list on the monolith-ui Table. GET /audits/audit-logs has no
// conditions envelope (search/sort/paging only), so unlike StorageTable /
// StoredDataTable there are no filter definitions or query adapter here.
const AuditLogsTable = ({ query, stateStoreKey }: AuditLogsTableProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const tableInstanceRef = useRef<TableInstance | undefined>(undefined);

  const tableState = useTableState<GetAuditLogsQuery>(
    query,
    stateStoreKey,
    DEFAULT_PAGE_SIZE
  );

  // Deterministic page boundaries: GET /audits/audit-logs applies no ORDER BY
  // when `order` is absent, so without a sort every LIMIT/OFFSET page is
  // separately unordered and rows can duplicate or vanish across
  // infinite-scroll pages (and across the export's server-side page loop).
  // created_on is in the server's sort whitelist.
  const resolvedQuery = useMemo<GetAuditLogsQuery>(
    () => ({
      ...tableState.resolvedQuery,
      ...(tableState.resolvedQuery.sort_by
        ? {}
        : { sort_by: "created_on", sort_dir: "desc" as const }),
    }),
    [tableState.resolvedQuery]
  );

  const {
    data,
    isLoading,
    isFetched,
    isFetchingNextPage,
    hasNextPage,
    isError,
    refetch,
    fetchNextPage,
  } = AuditLogsQuery.useGetInfiniteAuditLogs(resolvedQuery);

  // The queryFn validates page shape and throws on the API layer's resolved
  // failures, so pages here are well-formed; the optional chaining is just
  // defensive.
  const records = useMemo(
    () => data?.pages?.flatMap((page) => page?.data ?? []) ?? [],
    [data?.pages]
  );
  const totalRecords = data?.pages?.[0]?.total || 0;

  // Guard against repeated scroll-to-bottom events: an unconditional
  // fetchNextPage() cancels and restarts the in-flight page request
  // (cancelRefetch defaults to true), so continued scrolling can starve the
  // next page indefinitely.
  const debouncedFetchNextPage = useDebouncedCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, 50);

  const debouncedSearch = useDebouncedCallback((searchText: string) => {
    tableState.setSearchState(searchText);
  }, 300);

  const handleExportTable = (option: string) => {
    enqueueSnackbar("Exporting table...", { variant: "info" });

    let columns = (
      tableInstanceRef.current?.columnState?.length
        ? tableInstanceRef.current.columnState
        : AuditLogColumnDefinitions
    ).map((c) => ({
      dataField: c.dataField,
      header: c.caption || c.dataField,
      caption: c.caption || c.dataField,
      visible: c.visible,
      dataType: c.dataType,
    }));

    if (option === "export:visible") {
      columns = columns.filter((c) => c.visible !== false);
    }

    AuditsAPI.exportAuditLogsList({
      // The export service derives the xlsx filename from query.uuid (an
      // audit uuid). Pass it for audit-scoped lists; object-scoped exports
      // have no single audit, and the server falls back to the rows'
      // object_name (ENG-1546).
      query: {
        ...AuditLogsQuery.resolveAuditLogsParams(resolvedQuery),
        uuid: resolvedQuery.audit_uuid,
      },
      type: "xlsx",
      columns,
      date_format: getDateFormat({ isMoment: true, includeTime: true }),
      custom_date_format: getDateFormat({ isMoment: true, includeTime: false }),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
      .then((res: { signedUrl?: string; filename?: string } | undefined) => {
        // The API layer resolves failures as { success: false } and the
        // server resolves export errors as HTTP 200 { success: false } —
        // neither rejects, so check the shape explicitly instead of relying
        // on the destructure below to throw.
        if (!res?.signedUrl) {
          throw new Error("Failed to export audit logs");
        }
        const { signedUrl, filename } = res;
        const el = document.createElement("a");
        el.href = signedUrl.replace(
          "http://localhost:3000",
          "http://localhost:3001"
        );
        el.download = filename ?? "";
        el.click();
        el.remove();
      })
      .catch(() => {
        enqueueSnackbar("Failed to export audit logs.", { variant: "error" });
      });
  };

  // A failed *next-page* fetch also sets isError, but data still holds every
  // loaded page — don't unmount the table for that (it would drop the user's
  // scroll position and refetch all pages on Retry). Surface it and let the
  // next scroll-to-bottom retry via fetchNextPage.
  useEffect(() => {
    if (isError && data) {
      enqueueSnackbar("Failed to load more audit logs.", { variant: "error" });
    }
  }, [isError, data, enqueueSnackbar]);

  // An errored *initial* load would otherwise strand the tab on the loading
  // skeleton with no affordance beyond the global error toast — offer an
  // explicit retry instead (mirrors StoredDataTable).
  if (isError && !data) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: 10,
        }}
      >
        <div style={{ color: "red" }}>
          Failed to load audit logs. Please try again.
        </div>
        <Button size="xs" variant="subtle" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <StyledContent>
      <Table
        data={records}
        columnProps={AuditLogColumnDefinitions}
        keyField="uuid"
        virtualized={true}
        tableInstanceRef={tableInstanceRef}
        totalRecords={totalRecords}
        loading={isLoading || !isFetched || isFetchingNextPage}
        stateStorage={{
          enabled: true,
          key: stateStoreKey,
          type: "localStorage",
        }}
        onScrollToBottom={debouncedFetchNextPage}
        onColumnHeaderClick={({ sort }) =>
          tableState.setSortState(sort || undefined)
        }
        // No table-level enableSorting: monolith-ui ORs it with each column's
        // enableSorting flag, which would make every column sortable and let
        // non-whitelisted fields reach the server (which then drops its
        // ORDER BY). Sorting is opted in per column in the definitions.
        enableColumnReorder={true}
        enableColumnResize={true}
        manualSearch={true}
        manualSorting={true}
        manualExport={true}
        tableMenuOptions={{
          enabled: true,
          tableCountOptions: {
            recordsTotalEnabled: true,
          },
          columnSelectorOptions: {
            enabled: true,
          },
          searchOptions: {
            enabled: true,
            onSearch: debouncedSearch,
            // Server-side search only covers notes, created-by name, and
            // evidence/storage numbers — not the audit name — so the
            // placeholder names the searched fields instead of implying
            // everything visible is searchable.
            placeholder: "Search notes, user, item number...",
          },
          exportOptions: {
            enabled: true,
            onExport: handleExportTable,
          },
        }}
      />
    </StyledContent>
  );
};

export default AuditLogsTable;
